/**
 * The contacts list's view of f8's memory limits (PPL-350).
 *
 * Port of the app's hook, carrying the fixes its review produced.
 *
 * Three jobs: the slot counter, the once-shown lapse banner, and the filter that
 * separates the ppl f8 remembers from the ppl it has paused.
 *
 * ## The filter replaces a screen
 *
 * After a lapse two populations coexist and both must be reachable. Chips give
 * the second its own view without a new page, and without ever presenting them as
 * lost — changing which five is free, and it is pausing and resuming, not
 * deleting and re-adding.
 *
 * ## The chips must outlive the condition that showed them
 *
 * The filter and the chips are separate state, and letting the chips disappear
 * while a filter is still applied strands the list: an empty page with no control
 * to undo it. Both halves are handled — the chips render whenever a filter is on
 * (the caller's job), and the filter resets when its bucket empties (below).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import {
  fetchRememberQuota,
  rememberStateOf,
  type RememberQuota,
  type RememberState,
} from '../services/rememberService';

export type PeopleFilter = 'all' | 'active' | 'paused';

/**
 * @param rows the FULL contact list, not the searched subset. The chip counts are
 *   totals ("paused · 35"); deriving them from the filtered set would make the
 *   chips renumber — and the self-heal below fire — as the user types.
 */
export function useRememberList(uid: string | null | undefined, rows: unknown[] = []) {
  const [quota, setQuota] = useState<RememberQuota | null>(null);
  const [lapsed, setLapsed] = useState(false);
  const [filter, setFilter] = useState<PeopleFilter>('all');

  /**
   * Chip counts.
   *
   * `active` prefers the SERVER's number. The rows here come from a capped
   * subscription (200, ordered by first name), so on a larger address book a
   * locally-derived count can miss remembered ppl whose name sorts late — and it
   * would then disagree, visibly, with the slot pill sitting right beside it.
   * "remembering · 0" next to "no slots left" reads as broken.
   *
   * `paused` and `all` have no server equivalent and stay local, so they are a
   * floor rather than a total above the cap. `all` is clamped so the buckets can
   * never sum past it and make the row look wrong.
   */
  const counts = useMemo(() => {
    let localActive = 0;
    let paused = 0;
    rows.forEach((r) => {
      const s: RememberState = rememberStateOf(r);
      if (s === 'active') localActive += 1;
      else if (s === 'paused') paused += 1;
    });
    const active = typeof quota?.activeCount === 'number' ? quota.activeCount : localActive;
    return { all: Math.max(rows.length, active + paused), active, paused };
  }, [quota?.activeCount, rows]);

  /**
   * Self-heal the filter when its bucket empties.
   *
   * Without this the list strands: select `paused`, resume the last paused
   * person, and the filter still says `paused` while nothing matches — an empty
   * page that survives navigation.
   */
  useEffect(() => {
    if (filter === 'paused' && counts.paused === 0) setFilter('all');
    if (filter === 'active' && counts.active === 0) setFilter('all');
  }, [counts.active, counts.paused, filter]);

  const refreshQuota = useCallback(async () => {
    try {
      setQuota(await fetchRememberQuota());
    } catch {
      // Stale beats blank; blank beats wrong.
    }
  }, []);

  useEffect(() => { void refreshQuota(); }, [refreshQuota]);

  /**
   * Dismissed in THIS session, whatever the document says.
   *
   * Firestore answers a write from the local cache before the server does, and a
   * pending `serverTimestamp()` reads as null there — so the listener below would
   * immediately recompute "lapsed" as true and pop the banner straight back up
   * until the ack landed. Worse, `useAuth`'s 5-minute `lastAppOpen` heartbeat
   * writes this same document, so a REJECTED dismissal would bring the banner
   * back mid-session rather than "next visit".
   */
  const dismissedHere = useRef(false);

  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(
      doc(FIRESTORE_DB, `users/${uid}`),
      (snap) => {
        const d = (snap.exists() ? snap.data() : {}) || {};
        setLapsed(!dismissedHere.current && !!d.lapsedAt && !d.lapseBannerSeenAt);
      },
      () => setLapsed(false),
    );
  }, [uid]);

  const dismissLapse = useCallback(async () => {
    // Optimistic: it is a one-shot notice, and leaving it up while a write
    // round-trips is exactly the nag it exists to avoid.
    dismissedHere.current = true;
    setLapsed(false);
    if (!uid) return;
    try {
      await updateDoc(doc(FIRESTORE_DB, `users/${uid}`), { lapseBannerSeenAt: serverTimestamp() });
    } catch {
      // Stays down for this session and shows again on the next page load —
      // harmless, and better than a banner that vanishes for good on a failed
      // write, or one that reappears minutes later on an unrelated heartbeat.
    }
  }, [uid]);

  return { quota, counts, lapsed, filter, setFilter, dismissLapse, refreshQuota };
}

export default useRememberList;
